import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Building2,
  CreditCard,
  Mail,
  Activity,
  ShieldCheck,
  LayoutDashboard,
  Bot,
  LogOut,
  RefreshCw,
  LifeBuoy,
  Search,
} from "lucide-react";
import { api, money, date } from "../lib/backofficeApi";
import AssistantPanel from "../components/support/AssistantPanel";
import AgentOperationsPanel from "../components/AgentOperationsPanel";
import "./admin/backoffice.css";

const root = "/admin/backoffice";
const sections = [
  ["overview", "Resumen", LayoutDashboard],
  ["tenants", "Clientes y clínicas", Building2],
  ["billing", "Suscripciones", CreditCard],
  ["campaigns", "Comunicaciones", Mail],
  ["health", "Salud del sistema", Activity],
  ["operations", "Operaciones de IA", Bot],
  ["tickets", "Soporte", LifeBuoy],
  ["audit", "Auditoría", ShieldCheck],
  ["plans", "Planes", Bot],
];
const states = {
  active: "Activo",
  trial: "Prueba",
  trialing: "Prueba",
  past_due: "Pago pendiente",
  canceled: "Cancelado",
  suspended: "Suspendido",
  healthy: "Operativo",
  unhealthy: "Con falla",
  configured: "Configurado · sin prueba",
  not_configured: "Sin configurar",
  unknown: "Sin evidencia reciente",
  draft: "Borrador",
  queued: "En cola",
  queuing: "Preparando cola",
  delivered: "Entregado al servidor",
  accepted: "Aceptado por Resend",
  needs_review: "Requiere revisión",
  failed: "Fallido",
  bounced: "Rebotado",
  complained: "Queja",
  open: "Abierto",
  resolved: "Resuelto",
};
function Status({ value }) {
  return (
    <span
      className={`bo-status ${["active", "healthy", "delivered", "resolved"].includes(value) ? "good" : ["past_due", "unhealthy", "failed", "bounced", "needs_review"].includes(value) ? "bad" : ""}`}
    >
      {states[value] || value || "Sin datos"}
    </span>
  );
}
function Empty({ children = "No hay registros disponibles." }) {
  return <div className="bo-empty">{children}</div>;
}
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api("/auth/login", { email, password });
      localStorage.setItem("token", r.access_token);
      localStorage.setItem("clinicId", r.clinic_id);
      const s = await api(root + "/session");
      onLogin(s);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="bo-login">
      <form onSubmit={submit}>
        <span className="bo-eyebrow">AIMP · ADMINISTRACIÓN</span>
        <h1>
          Tu operación,
          <br />
          en un solo lugar.
        </h1>
        <p>Usa el correo y la contraseña de tu cuenta AIMP autorizada como administradora.</p>
        <label>
          Correo
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Contraseña
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && (
          <p role="alert" className="bo-error">
            {error}
          </p>
        )}
        <button className="bo-primary" disabled={busy}>
          {busy ? "Verificando…" : "Entrar al back office"}
        </button>
        <a href="/forgot-password">Recuperar contraseña</a>
      </form>
    </div>
  );
}
function ClientTable({ tenants, open, select, selected = [] }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const filtered = tenants.filter(
    (t) =>
      `${t.name} ${t.billing_email} ${t.plan}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (!status || t.subscription_status === status),
  );
  return (
    <>
      <div className="bo-toolbar">
        <label className="bo-search">
          <Search size={16} />
          <input
            aria-label="Buscar cliente"
            placeholder="Buscar por cliente, correo o plan"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <select
          aria-label="Filtrar estado"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Todos los estados</option>
          {["trial", "active", "past_due", "canceled"].map((s) => (
            <option key={s} value={s}>
              {states[s]}
            </option>
          ))}
        </select>
        <span>{filtered.length} clientes</span>
      </div>
      <div className="bo-table-wrap">
        <table>
          <thead>
            <tr>
              {select && <th>Elegir</th>}
              <th>Cliente / responsable</th>
              <th>Clínicas</th>
              <th>Clínica / WhatsApp registrado</th>
              <th>Plan</th>
              <th>Estado</th>
              <th>Mensualidad</th>
              <th>Próximo vencimiento</th>
              <th>Método de pago</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id}>
                {select && (
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${t.name}`}
                      checked={selected.includes(t.id)}
                      onChange={() => select(t.id)}
                    />
                  </td>
                )}
                <td>
                  <button className="bo-link" onClick={() => open(t.id)}>
                    {t.name}
                  </button>
                  <small>
                    {t.billing_email || "Sin correo"}
                    {t.is_test && " · PRUEBA INTERNA"}
                  </small>
                </td>
                <td>{t.clinics.length}</td>
                <td>
                  {t.clinics.map(c => <div key={c.id}>
                    <strong>{c.name}</strong>
                    <small>{c.whatsapp?.phone_number || "WhatsApp sin número guardado"}</small>
                    <small>{c.whatsapp?.is_connected ? "Conexión guardada · sin prueba actual" : "Sin conexión guardada"}</small>
                    <small>Contacto: {c.phone || "Sin teléfono"}</small>
                    <small>ID: {c.id}</small>
                  </div>)}
                </td>
                <td>{t.plan || "Sin asignar"}</td>
                <td>
                  <Status value={t.subscription_status} />
                </td>
                <td>{money(t.monthly_amount, t.currency || "mxn")}</td>
                <td>{date(t.current_period_end)}</td>
                <td>
                  {t.payment_method
                    ? `${t.payment_method.brand || t.payment_method.type} ···· ${t.payment_method.last4 || ""}`
                    : "Sin verificar"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filtered.length && <Empty />}
      </div>
    </>
  );
}
function Detail({ id, onClose, refresh }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [test, setTest] = useState(false);
  const [clinic, setClinic] = useState("");
  const [plan, setPlan] = useState("basico");
  const [catalog, setCatalog] = useState({});
  const [link, setLink] = useState(null);
  const load = useCallback(async () => {
    const r = await api(`${root}/tenant/${id}`);
    setData(r);
    setNotes(r.tenant.notes || "");
    setTest(!!r.tenant.is_test);
  }, [id]);
  useEffect(() => {
    load().catch((e) => setError(e.message));
    api(root + "/plans")
      .then((r) => setCatalog(r.plans))
      .catch((e) => setError(e.message));
  }, [load]);
  async function run(fn) {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="bo-detail">
      <div className="bo-section-head">
        <div>
          <span className="bo-eyebrow">FICHA DEL CLIENTE</span>
          <h2>{data?.tenant.name || "Cargando…"}</h2>
        </div>
        <button onClick={onClose}>Volver a clientes</button>
      </div>
      {error && (
        <p role="alert" className="bo-error">
          {error}
        </p>
      )}
      {data && (
        <>
          <div className="bo-metrics compact">
            {Object.entries(data.counts).map(([k, n]) => (
              <div key={k}>
                <span>
                  {
                    {
                      leads: "Prospectos",
                      appointments: "Citas",
                      messages: "Mensajes",
                    }[k]
                  }
                </span>
                <strong>{n}</strong>
              </div>
            ))}
          </div>
          <div className="bo-grid">
            <section className="bo-card">
              <h3>Cuenta y seguimiento</h3>
              <p>{data.tenant.billing_email}</p>
              <label>
                Notas internas
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
              <label className="bo-inline">
                <input
                  type="checkbox"
                  checked={test}
                  onChange={(e) => setTest(e.target.checked)}
                />{" "}
                Cuenta de prueba interna
              </label>
              <button
                disabled={busy || data.tenant.migration_required}
                onClick={() =>
                  run(() =>
                    api(
                      `${root}/tenant/${id}`,
                      { notes, is_test: test },
                      "PATCH",
                    ),
                  )
                }
              >
                Guardar notas
              </button>
              {data.tenant.migration_required && (
                <>
                  <p>
                    Esta clínica necesita vincularse a una organización para
                    administrar cobros.
                  </p>
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await api(
                          `${root}/clinic/${data.tenant.clinics[0].id}/organization`,
                          {},
                        );
                        onClose();
                      })
                    }
                  >
                    Vincular organización
                  </button>
                </>
              )}
            </section>
            <section className="bo-card">
              <h3>Suscripción y cobros</h3>
              <p>
                <Status value={data.tenant.subscription_status} /> ·{" "}
                {data.tenant.plan || "Sin plan"}
              </p>
              <p>
                Mensualidad verificada:{" "}
                <strong>
                  {money(
                    data.tenant.monthly_amount,
                    data.tenant.currency || "mxn",
                  )}
                </strong>
              </p>
              <p>Última conciliación: {date(data.tenant.billing_checked_at)}</p>
              <div className="bo-actions">
                <button
                  disabled={busy}
                  onClick={() =>
                    run(() => api(`${root}/tenant/${id}/billing/sync`, {}))
                  }
                >
                  Conciliar con Stripe
                </button>
                <button
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const r = await api(
                        `${root}/tenant/${id}/billing/portal`,
                        {},
                      );
                      setLink(r.url);
                    })
                  }
                >
                  Gestionar método y plan
                </button>
              </div>
              <label>
                Nuevo contrato
                <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                  {Object.entries(catalog).map(([key, item]) => (
                    <option key={key} value={key}>
                      {item.name} · {money(item.price)}/mes
                    </option>
                  ))}
                </select>
              </label>
              <button
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const r = await api(
                      `${root}/tenant/${id}/billing/checkout`,
                      { plan, operation_id: crypto.randomUUID() },
                    );
                    setLink(r.checkout_url);
                  })
                }
              >
                Crear enlace de contratación
              </button>
              {link && (
                <p>
                  <a href={link} target="_blank" rel="noreferrer">
                    Abrir enlace seguro de Stripe ↗
                  </a>
                </p>
              )}
            </section>
          </div>
          <section className="bo-card">
            <h3>Historial de mensualidades</h3>
            <div className="bo-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Comprobante</th>
                    <th>Estado</th>
                    <th>Cobrado</th>
                    <th>Pendiente</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.hosted_invoice_url ? (
                          <a
                            href={i.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {i.number || i.id}
                          </a>
                        ) : (
                          i.number || i.id
                        )}
                      </td>
                      <td>{i.status}</td>
                      <td>{money(i.amount_paid, i.currency)}</td>
                      <td>{money(i.amount_due, i.currency)}</td>
                      <td>{date(i.paid_at || i.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.invoices.length && (
                <Empty>
                  Sin comprobantes conciliados. No significa que el cliente haya
                  pagado.
                </Empty>
              )}
            </div>
          </section>
          <div className="bo-grid">
            <section className="bo-card">
              <h3>Usuarios</h3>
              {data.users.map((u) => (
                <p key={u.id || u.email}>
                  {u.email} <span className="bo-pill">{u.role}</span>
                </p>
              ))}
            </section>
            <section className="bo-card">
              <h3>Clínicas de esta organización</h3>
              {data.tenant.clinics.map((c) => (
                <p key={c.id}>
                  <button onClick={() => setClinic(c.id)}>
                    {c.name} · Abrir asistente
                  </button>
                  {c.registration_draft && <span className="bo-notice">
                    Alta incompleta del flujo retirado. El registro de una clínica nueva se realiza desde Sign Up.
                    <button disabled={busy} onClick={async () => {
                      setBusy(true); setError("");
                      try {
                        await api(`${root}/clinic/${c.id}/retire-registration-draft`, {});
                        await refresh();
                        onClose();
                      } catch (e) { setError(e.message); }
                      finally { setBusy(false); }
                    }}>Anular alta incompleta y conservar datos</button>
                  </span>}
                </p>
              ))}
            </section>
          </div>
          {clinic && <AssistantPanel key={clinic} clinicId={clinic} platform />}
        </>
      )}
    </div>
  );
}
function Campaigns({ tenants, items, refresh }) {
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("transactional");
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  async function run(fn) {
    setError("");
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="bo-grid">
        <section className="bo-card">
          <h2>Correo individual o masivo</h2>
          <p>
            Destinatarios: usuarios y responsables de los clientes
            seleccionados.
          </p>
          <label>
            Clientes
            <select
              aria-label="Clientes destinatarios"
              multiple
              value={selected}
              onChange={(e) => {
                setSelected(
                  Array.from(e.target.selectedOptions, (o) => o.value),
                );
                setDraft(null);
              }}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {t.billing_email}
                </option>
              ))}
            </select>
          </label>
          <label>
            Solo a un usuario (opcional)
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setDraft(null);
              }}
              placeholder="usuario de la organización seleccionada"
            />
          </label>
          <label>
            Tipo
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                setDraft(null);
              }}
            >
              <option value="transactional">Operativo / soporte</option>
              <option value="marketing">
                Promocional · con opción de baja
              </option>
            </select>
          </label>
          <label>
            Asunto
            <input
              value={subject}
              maxLength={200}
              onChange={(e) => {
                setSubject(e.target.value);
                setDraft(null);
              }}
            />
          </label>
          <label>
            Mensaje
            <textarea
              rows={7}
              value={body}
              maxLength={30000}
              onChange={(e) => {
                setBody(e.target.value);
                setDraft(null);
              }}
            />
          </label>
          <button
            className="bo-primary"
            disabled={
              busy || !selected.length || !body.trim() || !subject.trim()
            }
            onClick={() =>
              run(async () =>
                setDraft(
                  await api(root + "/campaigns", {
                    subject,
                    body,
                    kind,
                    tenant_ids: selected,
                    ...(email ? { recipient_emails: [email] } : {}),
                  }),
                ),
              )
            }
          >
            Preparar vista previa
          </button>
        </section>
        <section className="bo-card">
          <h3>Revisar antes de enviar</h3>
          {draft ? (
            <>
              <p>
                <strong>{draft.subject}</strong>
              </p>
              <p className="bo-whitespace">{draft.body}</p>
              <h4>{draft.recipients.length} destinatarios</h4>
              <ul>
                {draft.recipients.map((r) => (
                  <li key={r.email}>{r.email}</li>
                ))}
              </ul>
              <button
                className="bo-primary"
                disabled={busy || draft.status !== "draft"}
                onClick={() =>
                  run(async () => {
                    await api(`${root}/campaigns/${draft.id}/send`, {});
                    setDraft({ ...draft, status: "queued" });
                  })
                }
              >
                {draft.status === "draft"
                  ? "Confirmar envío a estos destinatarios"
                  : "En cola de envío"}
              </button>
            </>
          ) : (
            <Empty>
              Prepara el correo para comprobar su contenido y sus destinatarios
              exactos.
            </Empty>
          )}
          {error && (
            <p role="alert" className="bo-error">
              {error}
            </p>
          )}
        </section>
      </div>
      <section className="bo-card">
        <h3>Historial de comunicaciones</h3>
        {items.map((c) => (
          <article key={c.id} className="bo-row">
            <div>
              <strong>{c.subject}</strong>
              <small>
                {date(c.created_at)} · {c.recipients.length} destinatarios ·{" "}
                {c.kind}
              </small>
              <div className="bo-actions">
                {Object.entries(c.delivery).map(([s, n]) => (
                  <span key={s}>
                    {states[s] || s}: {n}
                  </span>
                ))}
              </div>
            </div>
            <Status value={c.status} />
            {["draft", "queued", "queuing"].includes(c.status) && (
              <button
                disabled={busy}
                onClick={() =>
                  run(() => api(`${root}/campaigns/${c.id}/cancel`, {}))
                }
              >
                Cancelar pendientes
              </button>
            )}
          </article>
        ))}
        {!items.length && <Empty />}
      </section>
    </>
  );
}
export default function Admin() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [section, setSection] = useState("overview");
  const [data, setData] = useState({});
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [operationRefresh, setOperationRefresh] = useState(0);
  const requestId = useRef(0);
  useEffect(() => {
    api(root + "/session")
      .then(setSession)
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);
  const load = useCallback(async () => {
    if (!session) return;
    if (section === "operations") {
      setOperationRefresh((value) => value + 1);
      setLoading(false);
      return;
    }
    const request = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const [clients, screen] = await Promise.all([
        api(root + "/tenants"),
        api(
          `${root}/${["tenants", "billing", "operations"].includes(section) ? "overview" : section}`,
        ),
      ]);
      if (request === requestId.current) {
        setTenants(clients.items);
        setData(screen);
      }
    } catch (e) {
      if (request === requestId.current) setError(e.message);
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, [session, section]);
  useEffect(() => {
    load();
  }, [load]);
  if (checking)
    return <div className="bo-scope bo-empty">Verificando acceso…</div>;
  if (!session)
    return (
      <div className="bo-scope">
        <Login onLogin={setSession} />
      </div>
    );
  async function logout() {
    try {
      await api("/auth/logout", {});
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("clinicId");
      setSession(null);
    }
  }
  return (
    <div className="bo-scope bo-shell">
      <aside className="bo-sidebar">
        <a className="bo-brand" href="/admin">
          <span>A</span>
          <div>
            AIMP<small>BACK OFFICE</small>
          </div>
        </a>
        <nav aria-label="Administración">
          {sections.map(([id, label, Icon]) => (
            <button
              key={id}
              className={section === id ? "active" : ""}
              onClick={() => {
                if (id === section) {
                  setSelected(null);
                  load();
                  return;
                }
                requestId.current++;
                setData({});
                setSection(id);
                setSelected(null);
              }}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </nav>
        <div className="bo-account">
          <small>{session.email}</small>
          <button onClick={logout}>
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="bo-main">
        <header className="bo-header">
          <div>
            <span className="bo-eyebrow">ADMINISTRACIÓN MULTICLÍNICA</span>
            <h1>{sections.find((s) => s[0] === section)?.[1]}</h1>
          </div>
          <button disabled={loading} onClick={load}>
            <RefreshCw size={16} />
            {loading ? "Actualizando…" : "Actualizar"}
          </button>
        </header>
        {error && (
          <p className="bo-error" role="alert">
            {error}
          </p>
        )}
        {selected ? (
          <Detail
            key={selected}
            id={selected}
            onClose={() => setSelected(null)}
            refresh={load}
          />
        ) : (
          <>
            {section === "overview" && (
              <>
                <p className="bo-intro">
                  Clientes, cobros y operación. Las cuentas de prueba se
                  excluyen de estos indicadores.
                </p>
                <div className="bo-metrics">
                  {[
                    ["Clientes", data.tenants],
                    ["Clínicas", data.clinics],
                    [
                      "Ingreso mensual verificado · MXN",
                      money(data.verified_mrr_mxn),
                    ],
                    ["En prueba", data.trial],
                    ["Pagos pendientes", data.past_due],
                    ["Casos abiertos", data.open_tickets],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>{value ?? "—"}</strong>
                    </div>
                  ))}
                </div>
                {data.unverified_billing > 0 && (
                  <div className="bo-notice">
                    {data.unverified_billing} clientes tienen facturación sin
                    verificar. Sus importes no se inventan ni se suman al
                    ingreso mensual.
                  </div>
                )}
                <section className="bo-card">
                  <h2>Tu cartera de clientes</h2>
                  <ClientTable tenants={tenants} open={setSelected} />
                </section>
              </>
            )}
            {["tenants", "billing"].includes(section) && (
              <section className="bo-card">
                <p>
                  {section === "billing"
                    ? "Abre un cliente para consultar comprobantes, conciliar cobros o gestionar su método de pago."
                    : "Cada organización agrupa sus clínicas, usuarios y contrato."}
                </p>
                <ClientTable tenants={tenants} open={setSelected} />
              </section>
            )}
            {section === "campaigns" && (
              <Campaigns
                tenants={tenants}
                items={data.items || []}
                refresh={load}
              />
            )}
            {section === "health" && (
              <>
                <p className="bo-intro">
                  Configurado no equivale a comprobado. La falta de evidencia se
                  muestra explícitamente.
                </p>
                <div className="bo-metrics compact">
                  {Object.entries(data.signals || {}).map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <div className="bo-grid">
                  {(data.checks || []).map((c) => (
                    <section className="bo-card" key={c.name}>
                      <h3>{c.name}</h3>
                      <Status value={c.status} />
                      <p>{c.detail}</p>
                      <small>Última comprobación: {date(c.checked_at)}</small>
                    </section>
                  ))}
                </div>
              </>
            )}
            {section === "operations" && <AgentOperationsPanel refreshKey={operationRefresh} />}
            {section === "tickets" && (
              <section className="bo-card">
                {(data.items || []).map((t) => (
                  <article className="bo-row" key={t.id}>
                    <div>
                      <strong>{t.context?.name || t.clinic_id}</strong>
                      <p>{t.message}</p>
                      <small>
                        {t.actor} · {date(t.created_at)}
                      </small>
                    </div>
                    <Status value={t.status} />
                    {t.status === "open" && (
                      <button
                        onClick={async () => {
                          try {
                            await api(`${root}/tickets/${t.id}/resolve`, {});
                            await load();
                          } catch (e) {
                            setError(e.message);
                          }
                        }}
                      >
                        Marcar resuelto
                      </button>
                    )}
                  </article>
                ))}
                {!data.items?.length && <Empty>Sin casos de soporte.</Empty>}
              </section>
            )}
            {section === "audit" && (
              <section className="bo-card">
                <h2>Actividad administrativa real</h2>
                {(data.items || []).map((a, i) => (
                  <article className="bo-row" key={a.id || i}>
                    <div>
                      <strong>
                        {a.action} · {a.resource_type}
                      </strong>
                      <small>
                        {a.user_email} · {date(a.created_at)}
                      </small>
                      <code>{a.resource_id}</code>
                    </div>
                  </article>
                ))}
                {!data.items?.length && <Empty />}
              </section>
            )}
            {section === "plans" && (
              <>
                <p>{data.notice}</p>
                <div className="bo-grid">
                  {Object.entries(data.plans || {}).map(([id, p]) => (
                    <section className="bo-card" key={id}>
                      <h2>{p.name}</h2>
                      <strong className="bo-price">
                        {money(p.price)}
                        <small>/mes</small>
                      </strong>
                      <p>{p.description}</p>
                      <p>
                        {p.max_users} usuarios ·{" "}
                        {p.max_conversations === -1
                          ? "Conversaciones ilimitadas"
                          : `${p.max_conversations} conversaciones`}
                      </p>
                      <ul>
                        {p.features.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
